<script setup lang="ts">
import type { RouteLocationRaw, RouteRecordNameGeneric } from 'vue-router'

import UpsellModuleModal from '@/components/UpsellModuleModal.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { SubItem } from '@/types/types'

export interface SidebarNavRouteConfig {
  key: string
  activeRoutes: RouteRecordNameGeneric[]
  name: string
  icon: Icons
  to?: RouteLocationRaw
  subItems?: SubItem[]
  upsellModuleType?: 'e-reputation' | 'pongo-suite'
  dropdownKey?: string
  showUpsellModal?: boolean
  showAsDisabled?: boolean
  iconAccent?: boolean
  onToggleDropdown?: () => void
}

export interface SidebarNavGroup {
  key: string
  labelKey?: string
  items: SidebarNavRouteConfig[]
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { hasWallet, moduleAccesses, subscriptions, isSupportConnection } = useLocalStorage()
const { loyaltyRouteName } = useLoyalty()
const { flags } = useFeatureFlag()

type CampaignSubMenuItem = SubItem & { query?: Record<string, string> }
const campaignsSubMenu = computed<SubItem[]>(() => {
  const campaigns: CampaignSubMenuItem[] = [
    {
      name: 'CampaignList',
      query: {
        type: 'sms'
      },
      matchRoutes: [
        { name: 'CampaignCreate', query: { type: 'sms' } },
        { name: 'CampaignCreate', query: { type: null } }
      ],
      translationKey: 'smsCampaignList'
    }
  ]
  let pushCampaignList: CampaignSubMenuItem = {
    name: 'CampaignList',
    query: {
      type: 'push'
    },
    matchRoutes: [{ name: 'CampaignCreate', query: { type: 'push' } }],
    translationKey: 'pushCampaignList'
  }

  if (!hasWallet.value) {
    pushCampaignList = { name: 'Wallet', translationKey: 'pushCampaignList', excludeFromActive: true }
  }

  campaigns.push({
    name: 'CampaignEmailListPage',
    matchRoutes: [{ name: 'CampaignEmailCreatePage' }, { name: 'CampaignEmailEditPage' }],
    translationKey: 'emailCampaignList'
  })

  const items: SubItem[] = [...campaigns, pushCampaignList]
  if (flags.value['offer-engine']?.enabled) {
    items.push({
      name: 'OfferList',
      matchRoutes: [{ name: 'OfferCreate' }, { name: 'OfferEdit' }],
      translationKey: 'promotionList'
    })
  }

  return items
})

const loyaltySubMenu = computed(() => {
  const items: SubItem[] = [{ name: loyaltyRouteName.value }, { name: 'TabletList' }, { name: 'Webapp' }]
  items.push({ name: 'CrmList' }, { name: 'Wallet' })

  return items
})

const eReputationSubMenu = computed(() => {
  return [
    {
      name: 'EReputation',
      params: { page: 'stores' },
      text: t('sidebar.eReputation.businesses')
    },
    {
      name: 'EReputation',
      params: { page: 'presenceManagement' },
      text: t('sidebar.eReputation.presenceManagement')
    },
    { name: 'EReputation', params: { page: 'reviews' }, text: t('sidebar.eReputation.reviews') },
    { name: 'EReputation', params: { page: 'posts' }, text: t('sidebar.eReputation.posts') },
    {
      name: 'EReputation',
      text: t('sidebar.eReputation.statistics'),
      subItems: [
        { params: { page: 'statistics' }, text: t('sidebar.eReputation.statisticsReviews') },
        { params: { page: 'analytics' }, text: t('sidebar.eReputation.statisticsVisibility') }
      ]
    },
    ...(isSupportConnection.value
      ? [{ name: 'EReputationStoreLocator', text: t('sidebar.eReputation.storeLocator') }]
      : []),
    {
      name: 'EReputation',
      params: { page: 'settings' },
      text: t('sidebar.eReputation.settings'),
      matchParams: [
        { page: 'settingsConnection' },
        { page: 'reviewAutoReplyWithoutComments' },
        { page: 'reviewAutoReplyWithComments' }
      ]
    }
  ]
})

/** Route names that make each nav section active (for SidebarNavItem activeRoutes) */
const activeRoutes = {
  home: ['Home'] as RouteRecordNameGeneric[],
  eReputation: ['EReputation', 'EReputationStoreLocator'] as RouteRecordNameGeneric[],
  deliveryReviews: ['EReputationDelivery'] as RouteRecordNameGeneric[],
  eReputationMenu: ['EReputationMenu'] as RouteRecordNameGeneric[],
  reviews: ['ReviewList', 'ReviewAnalytics', 'ReviewSettings'] as RouteRecordNameGeneric[],
  performances: [
    'Dashboard',
    'BusinessDashboard',
    'MarketingDashboard',
    'CustomersDashboard',
    'CockpitDashboard'
  ] as RouteRecordNameGeneric[],
  customers: ['CustomerList', 'CustomerDetail'] as RouteRecordNameGeneric[],
  audiences: [
    'AudienceList',
    'AudienceDetail',
    'AudienceDetails',
    'AudienceRules',
    'AudienceCreate'
  ] as RouteRecordNameGeneric[],
  surveys: ['SurveyList', 'SurveyDetail', 'SurveyEdit', 'SurveyCreate'] as RouteRecordNameGeneric[],
  rewards: ['RewardEdit'] as RouteRecordNameGeneric[],
  campaigns: [
    'CampaignList',
    'CampaignCreate',
    'CampaignDetail',
    'CampaignEmailListPage',
    'CampaignEmailCreatePage',
    'CampaignEmailEditPage',
    'OfferList',
    'OfferCreate',
    'OfferEdit'
  ] as RouteRecordNameGeneric[],
  autopilots: ['AutopilotList', 'AutopilotDetail', 'AutopilotCopyTemplate'] as RouteRecordNameGeneric[],
  contests: ['ContestList', 'ContestCreate', 'ContestDetail', 'ContestEdit'] as RouteRecordNameGeneric[],
  loyalty: ['LoyaltyEdit', 'LoyaltyCreate', 'CrmList', 'Webapp', 'TabletList', 'Wallet'] as RouteRecordNameGeneric[],
  missedCalls: ['MissedCallAnalytics', 'MissedCallsList', 'MissedCallEdit'] as RouteRecordNameGeneric[],
  integrations: ['IntegrationList'] as RouteRecordNameGeneric[]
}

const dropdownRoutes: Record<string, RouteRecordNameGeneric[]> = {
  showCampaigns: activeRoutes.campaigns,
  showLoyaltyProgram: activeRoutes.loyalty,
  showEReputaion: activeRoutes.eReputation,
  showReviews: activeRoutes.reviews,
  showMissedCalls: activeRoutes.missedCalls
}

const openDropdown = ref<string | null>(null)

const syncOpenDropdown = (name: RouteRecordNameGeneric) => {
  openDropdown.value = Object.keys(dropdownRoutes).find((key) => dropdownRoutes[key].includes(name)) ?? null
}

watch(route, (to) => syncOpenDropdown(to.name))

const toggleDropDown = (key: string) => {
  openDropdown.value = openDropdown.value === key ? null : key
}

syncOpenDropdown(route.name)

const hasEReputation = computed(() => moduleAccesses.value['e-reputation'] === true)
const hasReview = computed(() => subscriptions.value['pongo-suite'] === true || moduleAccesses.value['review'] === true)
const hasOnlyEReputation = computed(() => hasEReputation.value && !hasReview.value)
const hasOnlyReview = computed(() => hasReview.value && !hasEReputation.value)

const customerLocked = computed(() => moduleAccesses.value['customer'] !== true)

const upsellModalShown = ref<'e-reputation' | 'pongo-suite' | null>(null)

const openUpsellModal = (type: 'e-reputation' | 'pongo-suite') => {
  upsellModalShown.value = type
}

const eReputationItem = computed<SidebarNavRouteConfig | null>(() => {
  if (hasEReputation.value && hasReview.value) {
    const isOnEReputationPage = (['EReputation'] as RouteRecordNameGeneric[]).includes(route.name)
    const isOnReviewPage = (['ReviewList', 'ReviewAnalytics', 'ReviewSettings'] as RouteRecordNameGeneric[]).includes(
      route.name
    )

    if (isOnEReputationPage) {
      return {
        key: 'eReputation-dropdown',
        activeRoutes: activeRoutes.eReputation,
        name: t('sidebar.eReputation.menu'),
        icon: 'HeroBuildingStorefrontIconOutline',
        dropdownKey: 'showEReputaion',
        subItems: eReputationSubMenu.value,
        onToggleDropdown: () => router.push({ name: 'EReputationMenu' })
      }
    }
    if (isOnReviewPage) {
      return {
        key: 'reviews-dropdown',
        activeRoutes: activeRoutes.reviews,
        name: t('sidebar.eReputation.menu'),
        icon: 'HeroBuildingStorefrontIconOutline',
        dropdownKey: 'showReviews',
        subItems: [{ name: 'ReviewList' }, { name: 'ReviewAnalytics' }, { name: 'ReviewSettings' }],
        onToggleDropdown: () => router.push({ name: 'EReputationMenu' })
      }
    }
    return {
      key: 'eReputation-menu',
      activeRoutes: activeRoutes.eReputationMenu,
      name: t('sidebar.eReputation.menu'),
      icon: 'HeroBuildingStorefrontIconOutline',
      to: { name: 'EReputationMenu' }
    }
  } else if (hasOnlyEReputation.value) {
    return {
      key: 'eReputation-dropdown',
      activeRoutes: activeRoutes.eReputation,
      name: t('sidebar.eReputation.menu'),
      icon: 'HeroBuildingStorefrontIconOutline',
      dropdownKey: 'showEReputaion',
      subItems: eReputationSubMenu.value,
      onToggleDropdown: () => toggleDropDown('showEReputaion')
    }
  } else if (hasOnlyReview.value) {
    return {
      key: 'reviews-dropdown',
      activeRoutes: activeRoutes.reviews,
      name: t('sidebar.eReputation.menu'),
      icon: 'HeroBuildingStorefrontIconOutline',
      dropdownKey: 'showReviews',
      subItems: [{ name: 'ReviewList' }, { name: 'ReviewAnalytics' }, { name: 'ReviewSettings' }],
      onToggleDropdown: () => toggleDropDown('showReviews')
    }
  }
  return null
})

const sidebarGroups = computed<SidebarNavGroup[]>(() => {
  const main: SidebarNavRouteConfig[] = [
    {
      key: 'home',
      activeRoutes: activeRoutes.home,
      name: t('sidebar.assistant'),
      icon: 'HeroSparklesIconOutline',
      iconAccent: true,
      to: { name: 'Home' }
    }
  ]

  const clients: SidebarNavRouteConfig[] = [
    {
      key: 'customers',
      activeRoutes: activeRoutes.customers,
      name: t('sidebar.customerList'),
      icon: 'HeroCircleStackIconOutline',
      to: { name: 'CustomerList' },
      showUpsellModal: customerLocked.value,
      showAsDisabled: customerLocked.value,
      upsellModuleType: 'pongo-suite'
    },
    {
      key: 'audiences',
      activeRoutes: activeRoutes.audiences,
      name: t('sidebar.audienceList'),
      icon: 'HeroUserGroupIconOutline',
      to: { name: 'AudienceList' },
      showUpsellModal: customerLocked.value,
      showAsDisabled: customerLocked.value,
      upsellModuleType: 'pongo-suite'
    }
  ]

  const animation: SidebarNavRouteConfig[] = []
  if (subscriptions.value['pongo-suite'] === true || moduleAccesses.value['reward'] === true) {
    animation.push({
      key: 'rewards',
      activeRoutes: activeRoutes.rewards,
      name: t('sidebar.rewards'),
      icon: 'HeroGiftIconOutline',
      to: { name: 'RewardEdit' },
      showUpsellModal: moduleAccesses.value['reward'] !== true,
      showAsDisabled: moduleAccesses.value['reward'] !== true,
      upsellModuleType: 'pongo-suite'
    })
  }
  animation.push({
    key: 'loyalty',
    activeRoutes: activeRoutes.loyalty,
    name: t('sidebar.loyalty.menu'),
    icon: 'HeroHeartIconOutline',
    dropdownKey: 'showLoyaltyProgram',
    subItems: loyaltySubMenu.value,
    showUpsellModal: moduleAccesses.value['reward'] !== true,
    showAsDisabled: moduleAccesses.value['reward'] !== true,
    upsellModuleType: 'pongo-suite',
    onToggleDropdown: () => toggleDropDown('showLoyaltyProgram')
  })
  animation.push({
    key: 'contests',
    activeRoutes: activeRoutes.contests,
    name: t('sidebar.contests'),
    icon: 'HeroTrophyIconOutline',
    to: { name: 'ContestList' },
    showUpsellModal: moduleAccesses.value['contest-game'] !== true,
    showAsDisabled: moduleAccesses.value['contest-game'] !== true,
    upsellModuleType: 'pongo-suite'
  })
  animation.push({
    key: 'surveys',
    activeRoutes: activeRoutes.surveys,
    name: t('sidebar.surveyList'),
    icon: 'HeroChatBubbleBottomCenterTextIconOutline',
    to: { name: 'SurveyList' },
    showUpsellModal: customerLocked.value,
    showAsDisabled: customerLocked.value,
    upsellModuleType: 'pongo-suite'
  })
  animation.push({
    key: 'addons',
    activeRoutes: ['AddonsList'] as RouteRecordNameGeneric[],
    name: t('sidebar.addons'),
    icon: 'HeroSquaresPlusIconOutline',
    to: { name: 'AddonsList' }
  })

  const vitrine: SidebarNavRouteConfig[] = []
  // Support connections only for now: the e-reputation menu pages are managed by Partoo, this one by Pongo.
  if (isSupportConnection.value && hasEReputation.value) {
    vitrine.push({
      key: 'deliveryReviews',
      activeRoutes: activeRoutes.deliveryReviews,
      name: t('sidebar.eReputation.deliveryPlatforms'),
      icon: 'HeroTruckIconOutline',
      to: { name: 'EReputationDelivery' }
    })
  }
  if (eReputationItem.value) vitrine.push(eReputationItem.value)
  vitrine.push({
    key: 'missedCalls',
    activeRoutes: activeRoutes.missedCalls,
    name: t('sidebar.missedCalls.menu'),
    icon: 'HeroPhoneXMarkIconOutline',
    dropdownKey: 'showMissedCalls',
    subItems: [{ name: 'MissedCallAnalytics' }, { name: 'MissedCallsList', matchRoutes: [{ name: 'MissedCallEdit' }] }],
    showUpsellModal: moduleAccesses.value['missed-call'] !== true,
    showAsDisabled: moduleAccesses.value['missed-call'] !== true,
    upsellModuleType: 'pongo-suite',
    onToggleDropdown: () => toggleDropDown('showMissedCalls')
  })

  const campaigns: SidebarNavRouteConfig[] = [
    {
      key: 'campaigns',
      activeRoutes: activeRoutes.campaigns,
      name: t('sidebar.campaigns.menu'),
      icon: 'HeroMegaphoneIconOutline',
      dropdownKey: 'showCampaigns',
      subItems: campaignsSubMenu.value,
      showUpsellModal: moduleAccesses.value['campaign'] !== true,
      showAsDisabled: moduleAccesses.value['campaign'] !== true,
      upsellModuleType: 'pongo-suite',
      onToggleDropdown: () => toggleDropDown('showCampaigns')
    },
    {
      key: 'autopilots',
      activeRoutes: activeRoutes.autopilots,
      name: t('sidebar.autopilots'),
      icon: 'HeroBoltIconOutline',
      to: { name: 'AutopilotList' },
      showUpsellModal: moduleAccesses.value['autopilot'] !== true,
      showAsDisabled: moduleAccesses.value['autopilot'] !== true,
      upsellModuleType: 'pongo-suite'
    }
  ]

  const results: SidebarNavRouteConfig[] = []
  if (subscriptions.value['pongo-suite'] === true || moduleAccesses.value['dashboard'] === true) {
    results.push({
      key: 'dashboard',
      activeRoutes: activeRoutes.performances,
      name: t('sidebar.performances.menu'),
      icon: 'HeroChartPieIconOutline',
      to: { name: 'CockpitDashboard' },
      showUpsellModal: moduleAccesses.value['dashboard'] !== true,
      showAsDisabled: moduleAccesses.value['dashboard'] !== true,
      upsellModuleType: 'pongo-suite'
    })
  }

  const settings: SidebarNavRouteConfig[] = []
  if (subscriptions.value['pongo-suite'] === true || moduleAccesses.value['marketplace'] === true) {
    settings.push({
      key: 'integrations',
      activeRoutes: activeRoutes.integrations,
      name: t('sidebar.integrations'),
      icon: 'HeroLinkIconOutline',
      to: { name: 'IntegrationList' },
      showUpsellModal: moduleAccesses.value['marketplace'] !== true,
      showAsDisabled: moduleAccesses.value['marketplace'] !== true,
      upsellModuleType: 'pongo-suite'
    })
  }

  return [
    { key: 'main', items: main },
    { key: 'clients', labelKey: 'sidebar.groups.clients', items: clients },
    { key: 'results', labelKey: 'sidebar.groups.results', items: results },
    { key: 'campaigns', labelKey: 'sidebar.groups.campaigns', items: campaigns },
    { key: 'animation', labelKey: 'sidebar.groups.animation', items: animation },
    { key: 'vitrine', labelKey: 'sidebar.groups.vitrine', items: vitrine },
    { key: 'settings', labelKey: 'sidebar.groups.settings', items: settings }
  ]
})
</script>

<template>
  <nav class="relative flex min-h-full flex-1 flex-col px-2 md:min-w-[228px]">
    <template v-for="group in sidebarGroups" :key="group.key">
      <div v-if="group.items.length" class="space-y-1" :class="group.labelKey ? 'mt-6 first:mt-0' : ''">
        <p v-if="group.labelKey" class="px-3 pb-1 text-xs font-medium text-foreground-disabled">
          {{ t(group.labelKey) }}
        </p>
        <SidebarNavItem
          v-for="item in group.items"
          :key="item.key"
          :active-routes="item.activeRoutes"
          :name="item.name"
          :icon="item.icon"
          :icon-accent="item.iconAccent ?? false"
          :to="item.to"
          :sub-items="item.subItems"
          :show-upsell-modal="item.showUpsellModal ?? false"
          :upsell-module-type="item.upsellModuleType ?? 'e-reputation'"
          :is-dropdown-open="item.dropdownKey ? openDropdown === item.dropdownKey : false"
          @toggle-dropdown="item.onToggleDropdown?.()"
          @open-upsell="openUpsellModal"
        />
      </div>
    </template>
  </nav>

  <UpsellModuleModal :show="upsellModalShown !== null" :module="upsellModalShown" @close="upsellModalShown = null" />
</template>
